import { useState, useEffect, useRef } from 'react';
import { getSettings, updateSettings, getTemplates, saveSMTPSettings, testSMTPSettings, getBankBalance, updateBankBalance } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Loader2, Building2, Upload, FileText, CreditCard, Receipt, Palette, Check, Mail, Send, Eye, EyeOff, CheckCircle, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSMTP, setSavingSMTP] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [templates, setTemplates] = useState([]);
  const fileInputRef = useRef(null);
  const [bankBalance, setBankBalance] = useState({ amount: 0, updated_at: null });
  const [bankBalanceInput, setBankBalanceInput] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '', logo_base64: '', address: '', city: '', postal_code: '', country: 'Suisse',
    phone: '', email: '', commerce_number: '', vat_number: '', iban: '', payment_terms: '', legal_mentions: '',
    vat_enabled: true, default_vat_rate: 7.7, default_currency: 'CHF', invoice_template: 'blue_corporate',
    invoice_prefix: 'FAC', invoice_digits: 4, invoice_format: '{PREFIX}-{YEAR}-{NUM}',
    quote_prefix: 'DEV', quote_digits: 4, quote_format: '{PREFIX}-{YEAR}-{NUM}'
  });
  const [smtpData, setSMTPData] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', 
    smtp_from_email: '', smtp_from_name: '', smtp_use_tls: true, smtp_configured: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [settingsRes, templatesRes] = await Promise.all([getSettings(), getTemplates()]);
      const data = settingsRes.data;
      setFormData({
        company_name: data.company_name || '',
        logo_base64: data.logo_base64 || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        country: data.country || 'Suisse',
        phone: data.phone || '',
        email: data.email || '',
        commerce_number: data.commerce_number || '',
        vat_number: data.vat_number || '',
        iban: data.iban || '',
        payment_terms: data.payment_terms || '',
        legal_mentions: data.legal_mentions || '',
        vat_enabled: data.vat_enabled !== false,
        default_vat_rate: data.default_vat_rate || 7.7,
        default_currency: data.default_currency || 'CHF',
        invoice_template: data.invoice_template || 'blue_corporate'
      });
      // Load SMTP settings
      setSMTPData({
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || '',
        smtp_password: data.smtp_password || '',
        smtp_from_email: data.smtp_from_email || '',
        smtp_from_name: data.smtp_from_name || data.company_name || '',
        smtp_use_tls: data.smtp_use_tls !== false,
        smtp_configured: data.smtp_configured || false
      });
      if (data.logo_base64) setLogoPreview(data.logo_base64);
      setTemplates(templatesRes.data);
      // Load bank balance
      try {
        const balRes = await getBankBalance();
        setBankBalance(balRes.data);
        setBankBalanceInput(balRes.data.amount?.toString() || '0');
      } catch (err) { console.error('Failed to load bank balance:', err); }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData({ ...formData, logo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBankBalance = async () => {
    setSavingBalance(true);
    try {
      const amount = parseFloat(bankBalanceInput.replace(/[^0-9.,\-]/g, '').replace(',', '.'));
      if (isNaN(amount)) { toast.error('Montant invalide'); setSavingBalance(false); return; }
      const res = await updateBankBalance({ amount });
      setBankBalance(res.data);
      toast.success('Solde bancaire mis a jour');
    } catch { toast.error('Erreur'); } finally { setSavingBalance(false); }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('Parametres enregistres');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const SaveSectionButton = () => (
    <div className="mt-4 flex justify-end">
      <Button onClick={() => handleSubmit()} disabled={saving} className="bg-blue-600 hover:bg-blue-700" size="sm">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : <><Save className="w-4 h-4 mr-2" />Enregistrer</>}
      </Button>
    </div>
  );

  const handleSaveSMTP = async () => {
    if (!smtpData.smtp_host || !smtpData.smtp_user || !smtpData.smtp_password || !smtpData.smtp_from_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSavingSMTP(true);
    try {
      await saveSMTPSettings({
        smtp_host: smtpData.smtp_host,
        smtp_port: smtpData.smtp_port,
        smtp_user: smtpData.smtp_user,
        smtp_password: smtpData.smtp_password,
        smtp_from_email: smtpData.smtp_from_email,
        smtp_from_name: smtpData.smtp_from_name || formData.company_name,
        smtp_use_tls: smtpData.smtp_use_tls
      });
      setSMTPData({ ...smtpData, smtp_configured: true });
      toast.success('Configuration SMTP enregistrée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSMTP(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!smtpData.smtp_host || !smtpData.smtp_user || !smtpData.smtp_password || !smtpData.smtp_from_email) {
      toast.error('Veuillez remplir tous les champs avant de tester');
      return;
    }
    setTestingSMTP(true);
    try {
      await testSMTPSettings({
        smtp_host: smtpData.smtp_host,
        smtp_port: smtpData.smtp_port,
        smtp_user: smtpData.smtp_user,
        smtp_password: smtpData.smtp_password,
        smtp_from_email: smtpData.smtp_from_email,
        smtp_use_tls: smtpData.smtp_use_tls
      });
      toast.success('Email de test envoyé ! Vérifiez votre boîte de réception.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion SMTP');
    } finally {
      setTestingSMTP(false);
    }
  };

  // Group templates by style
  const templateGroups = {
    modern: templates.filter(t => t.style === 'modern'),
    classic: templates.filter(t => t.style === 'classic'),
    minimal: templates.filter(t => t.style === 'minimal'),
    gradient: templates.filter(t => t.style === 'gradient'),
    swiss: templates.filter(t => t.style === 'swiss'),
    other: templates.filter(t => ['tech', 'artisan'].includes(t.style))
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">{t('settings.title')}</h1>
        <p className="text-slate-500 mt-1">{language === 'fr' ? 'Configurez votre entreprise et vos préférences de facturation' : language === 'de' ? 'Konfigurieren Sie Ihr Unternehmen und Ihre Rechnungseinstellungen' : 'Configure your company and invoicing preferences'}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selection */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center"><Palette className="w-5 h-5 text-pink-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">{t('settings.pdfTemplate')}</CardTitle>
                <CardDescription>{language === 'fr' ? 'Choisissez le design de vos factures et devis' : language === 'de' ? 'Wählen Sie das Design Ihrer Rechnungen und Offerten' : 'Choose the design for your invoices and quotes'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Modernes */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">{language === 'fr' ? 'Modernes' : language === 'de' ? 'Modern' : 'Modern'}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.modern.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="h-16 rounded mb-2 flex items-center justify-center" style={{ backgroundColor: t.accent }}>
                      <div className="w-full h-2 mx-2 rounded" style={{ backgroundColor: t.primary }}></div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Classiques */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Classiques</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.classic.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="h-16 rounded mb-2 flex flex-col" style={{ backgroundColor: '#fff' }}>
                      <div className="h-4 rounded-t" style={{ backgroundColor: t.primary }}></div>
                      <div className="flex-1 p-1">
                        <div className="w-full h-1 mb-1 rounded" style={{ backgroundColor: t.accent }}></div>
                        <div className="w-2/3 h-1 rounded" style={{ backgroundColor: t.accent }}></div>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Minimalistes */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Minimalistes</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.minimal.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="h-16 rounded mb-2 flex flex-col p-2" style={{ backgroundColor: t.accent }}>
                      <div className="w-1/2 h-1 mb-2 rounded" style={{ backgroundColor: t.primary }}></div>
                      <div className="w-full h-0.5 mb-1" style={{ backgroundColor: t.secondary, opacity: 0.3 }}></div>
                      <div className="w-full h-0.5 mb-1" style={{ backgroundColor: t.secondary, opacity: 0.3 }}></div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Créatifs (Gradients) */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Créatifs</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.gradient.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div 
                      className="h-16 rounded mb-2"
                      style={{ background: `linear-gradient(135deg, ${t.primary} 0%, ${t.secondary} 100%)` }}
                    >
                      <div className="h-full flex items-center justify-center">
                        <div className="w-8 h-8 bg-white/20 rounded"></div>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Suisse */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Style Suisse</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.swiss.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="h-16 rounded mb-2 border" style={{ borderColor: t.primary, backgroundColor: '#fff' }}>
                      <div className="h-3 flex items-center px-2" style={{ backgroundColor: t.primary }}>
                        <div className="w-2 h-2 bg-white rounded-sm"></div>
                      </div>
                      <div className="p-1">
                        <div className="w-full h-1 mb-1 rounded" style={{ backgroundColor: t.accent }}></div>
                        <div className="w-2/3 h-1 rounded" style={{ backgroundColor: t.accent }}></div>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Industrie */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Industrie</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {templateGroups.other.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_template: t.id })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      formData.invoice_template === t.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="h-16 rounded mb-2" style={{ backgroundColor: t.accent }}>
                      <div className="h-full flex flex-col justify-between p-2">
                        <div className="w-1/2 h-2 rounded" style={{ backgroundColor: t.primary }}></div>
                        <div className="flex gap-1">
                          <div className="flex-1 h-1 rounded" style={{ backgroundColor: t.secondary, opacity: 0.5 }}></div>
                          <div className="w-4 h-1 rounded" style={{ backgroundColor: t.primary }}></div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs font-medium text-slate-700 truncate">{t.name}</p>
                    {formData.invoice_template === t.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <SaveSectionButton />
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-blue-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">Informations entreprise</CardTitle>
                <CardDescription>Apparaissent sur vos factures</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="w-24 h-24 rounded-lg border overflow-hidden bg-white flex items-center justify-center">
                    <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed flex items-center justify-center bg-slate-50">
                    <Building2 className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleLogoUpload} accept="image/*" className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />{logoPreview ? 'Changer' : 'Télécharger'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">PNG, JPG max 2MB</p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label>Nom de l'entreprise</Label>
                <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} data-testid="company-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Adresse</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>N° Registre du Commerce</Label>
                <Input value={formData.commerce_number} onChange={(e) => setFormData({ ...formData, commerce_number: e.target.value })} placeholder="CHE-XXX.XXX.XXX" data-testid="commerce-number-input" />
              </div>
              <div className="space-y-2">
                <Label>N° TVA</Label>
                <Input value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} placeholder="CHE-XXX.XXX.XXX TVA" />
              </div>
            </div>
            <SaveSectionButton />
          </CardContent>
        </Card>

        {/* TVA Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center"><Receipt className="w-5 h-5 text-purple-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">TVA & Devise</CardTitle>
                <CardDescription>Configuration fiscale adaptable selon votre pays</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Assujetti à la TVA</p>
                <p className="text-sm text-slate-500">Désactivez si vous n'êtes pas assujetti</p>
              </div>
              <Switch checked={formData.vat_enabled} onCheckedChange={(checked) => setFormData({ ...formData, vat_enabled: checked })} />
            </div>
            
            {!formData.vat_enabled && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Non assujetti à la TVA</strong> - Cette mention apparaîtra automatiquement sur vos factures.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Taux TVA par défaut (%)</Label>
                <Input type="number" step="0.1" value={formData.default_vat_rate} onChange={(e) => setFormData({ ...formData, default_vat_rate: parseFloat(e.target.value) || 0 })} disabled={!formData.vat_enabled} data-testid="default-vat-rate-input" />
                <p className="text-xs text-slate-500">
                  Exemples: Suisse 8.1%, France 20%, Allemagne 19%, Belgique 21%
                </p>
              </div>
              <div className="space-y-2">
                <Label>Devise par défaut</Label>
                <Select value={formData.default_currency} onValueChange={(v) => setFormData({ ...formData, default_currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHF">CHF - Franc suisse</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="USD">USD - Dollar américain</SelectItem>
                    <SelectItem value="GBP">GBP - Livre sterling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.vat_enabled && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Conseil :</strong> Vous pouvez modifier le taux TVA individuellement sur chaque facture ou devis lors de la création.
                </p>
              </div>
            )}
            <SaveSectionButton />
          </CardContent>
        </Card>

        {/* Banking */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CreditCard className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">Coordonnées bancaires</CardTitle>
                <CardDescription>Pour le paiement</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input value={formData.iban} onChange={(e) => setFormData({ ...formData, iban: e.target.value })} placeholder="CH00 0000 0000 0000 0000 0" data-testid="iban-input" />
              {formData.iban && /^(CH|LI)\d{19}$/.test(formData.iban.replace(/\s/g, '').toUpperCase()) && formData.company_name && formData.postal_code && formData.city ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1" data-testid="qr-bill-ready-indicator">
                  <CheckCircle className="w-3 h-3" /> QR-facture suisse active - sera ajoutee automatiquement aux PDFs
                </p>
              ) : formData.iban ? (
                <p className="text-xs text-amber-600 mt-1" data-testid="qr-bill-incomplete-indicator">
                  Completez IBAN (format CH/LI), nom entreprise, code postal et ville pour activer la QR-facture suisse
                </p>
              ) : null}
            </div>
            <SaveSectionButton />
          </CardContent>
        </Card>

        {/* Numbering / Numérotation */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center"><FileText className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">Numerotation</CardTitle>
                <CardDescription>Format des numeros de factures et devis</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="font-semibold">Factures</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Prefixe</Label>
                    <Input value={formData.invoice_prefix} onChange={(e) => setFormData({...formData, invoice_prefix: e.target.value})} placeholder="FAC" data-testid="invoice-prefix" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Nb chiffres</Label>
                    <Select value={String(formData.invoice_digits)} onValueChange={(v) => setFormData({...formData, invoice_digits: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 (001)</SelectItem>
                        <SelectItem value="4">4 (0001)</SelectItem>
                        <SelectItem value="5">5 (00001)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-400">Apercu : <span className="font-mono text-slate-600">{formData.invoice_prefix}-{new Date().getFullYear()}-{'0'.repeat(Math.max(0, formData.invoice_digits - 1))}1</span></p>
              </div>
              <div className="space-y-3">
                <Label className="font-semibold">Devis</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Prefixe</Label>
                    <Input value={formData.quote_prefix} onChange={(e) => setFormData({...formData, quote_prefix: e.target.value})} placeholder="DEV" data-testid="quote-prefix" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Nb chiffres</Label>
                    <Select value={String(formData.quote_digits)} onValueChange={(v) => setFormData({...formData, quote_digits: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 (001)</SelectItem>
                        <SelectItem value="4">4 (0001)</SelectItem>
                        <SelectItem value="5">5 (00001)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-400">Apercu : <span className="font-mono text-slate-600">{formData.quote_prefix}-{new Date().getFullYear()}-{'0'.repeat(Math.max(0, formData.quote_digits - 1))}1</span></p>
              </div>
            </div>
            <SaveSectionButton />
          </CardContent>
        </Card>

        {/* Bank Balance */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Landmark className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">Solde bancaire</CardTitle>
                <CardDescription>Saisissez votre solde actuel pour le calcul de tresorerie</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <Label>Montant actuel (CHF)</Label>
                <Input
                  type="text"
                  value={bankBalanceInput}
                  onChange={(e) => setBankBalanceInput(e.target.value)}
                  placeholder="0.00"
                  data-testid="settings-bank-balance-input"
                />
              </div>
              <Button onClick={handleSaveBankBalance} disabled={savingBalance} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-bank-balance-btn">
                {savingBalance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
            {bankBalance.updated_at && (
              <p className="text-xs text-slate-500">Derniere mise a jour : {new Date(bankBalance.updated_at).toLocaleDateString('fr-CH')} a {new Date(bankBalance.updated_at).toLocaleTimeString('fr-CH', {hour:'2-digit', minute:'2-digit'})}</p>
            )}
            <p className="text-xs text-slate-400">Ce montant sera utilise dans le Bilan Fiscal pour calculer votre tresorerie previsionnelle.</p>
          </CardContent>
        </Card>

        {/* Textes par defaut */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><FileText className="w-5 h-5 text-amber-600" /></div>
              <div>
                <CardTitle className="font-heading text-lg">Textes par défaut</CardTitle>
                <CardDescription>Apparaissent sur les factures</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Conditions de paiement</Label>
              <Textarea value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} rows={2} placeholder="Ex: Paiement à 30 jours net" data-testid="payment-terms-input" />
            </div>
            <div className="space-y-2">
              <Label>Mentions légales</Label>
              <Textarea value={formData.legal_mentions} onChange={(e) => setFormData({ ...formData, legal_mentions: e.target.value })} rows={3} placeholder="Mentions légales..." data-testid="legal-mentions-input" />
            </div>
            <SaveSectionButton />
          </CardContent>
        </Card>
      </form>

      {/* SMTP Email Configuration - Outside main form */}
      <Card className="border-slate-200 mt-6">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Configuration Email (SMTP)</CardTitle>
              <CardDescription>Envoyez vos factures et devis par email directement depuis votre adresse</CardDescription>
            </div>
          </div>
          {smtpData.smtp_configured && (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Check className="w-3 h-3 mr-1" /> Configuré
              </span>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Pourquoi configurer ?</strong> Vos factures seront envoyées depuis VOTRE adresse email, pas celle de Xyron Core ERP.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Serveur SMTP *</Label>
              <Input 
                value={smtpData.smtp_host} 
                onChange={(e) => setSMTPData({ ...smtpData, smtp_host: e.target.value })} 
                placeholder="smtp.gmail.com"
                data-testid="smtp-host-input"
              />
              <p className="text-xs text-slate-500">Ex: smtp.gmail.com, mail.infomaniak.com</p>
            </div>
            <div className="space-y-2">
              <Label>Port *</Label>
              <Select value={String(smtpData.smtp_port)} onValueChange={(v) => setSMTPData({ ...smtpData, smtp_port: parseInt(v) })}>
                <SelectTrigger data-testid="smtp-port-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="587">587 (TLS - Recommandé)</SelectItem>
                  <SelectItem value="465">465 (SSL)</SelectItem>
                  <SelectItem value="25">25 (Non sécurisé)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom d'utilisateur SMTP *</Label>
              <Input 
                value={smtpData.smtp_user} 
                onChange={(e) => setSMTPData({ ...smtpData, smtp_user: e.target.value })} 
                placeholder="votre-email@exemple.ch"
                data-testid="smtp-user-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe / Clé API *</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? "text" : "password"}
                  value={smtpData.smtp_password} 
                  onChange={(e) => setSMTPData({ ...smtpData, smtp_password: e.target.value })} 
                  placeholder="••••••••"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Pour Gmail, utilisez un mot de passe d'application</p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email expéditeur *</Label>
              <Input 
                type="email"
                value={smtpData.smtp_from_email} 
                onChange={(e) => setSMTPData({ ...smtpData, smtp_from_email: e.target.value })} 
                placeholder="factures@votre-entreprise.ch"
                data-testid="smtp-from-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nom d'affichage</Label>
              <Input 
                value={smtpData.smtp_from_name} 
                onChange={(e) => setSMTPData({ ...smtpData, smtp_from_name: e.target.value })} 
                placeholder={formData.company_name || "Votre Entreprise"}
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Utiliser TLS</p>
              <p className="text-sm text-slate-500">Recommandé pour la sécurité</p>
            </div>
            <Switch 
              checked={smtpData.smtp_use_tls} 
              onCheckedChange={(checked) => setSMTPData({ ...smtpData, smtp_use_tls: checked })}
              data-testid="smtp-tls-switch"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleTestSMTP} 
              disabled={testingSMTP}
              data-testid="test-smtp-btn"
            >
              {testingSMTP ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Tester la configuration</>
              )}
            </Button>
            <Button 
              type="button" 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleSaveSMTP} 
              disabled={savingSMTP}
              data-testid="save-smtp-btn"
            >
              {savingSMTP ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Enregistrer SMTP</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
